import React, { useMemo } from "react";
import {
  MaterialReactTable,
  MRT_ExpandButton,
  useMaterialReactTable,
} from "material-react-table";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useMIS } from "../MISContext";

const STATUS_OPTIONS = ["Open", "In Progress", "Closed"];

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export default function DeviationTable() {
  const {
    deviationRows,
    deviationLoading,
    deviationSaving,
    updateDeviationRow,
  } = useMIS();

  const columns = useMemo(
    () => [
      { accessorKey: "category", header: "Category" },

      {
        accessorKey: "referenceSource",
        header: "Reference (Source)",
      },
      {
        accessorKey: "budgeted",
        header: (
          <Box sx={{ lineHeight: 1.2 }}>
            <div>Budgeted</div>
            <div style={{ fontSize: "0.75rem", marginTop: "2px" }}>
              (hrs or $)
            </div>
          </Box>
        ),
      },
      {
        accessorKey: "actual",
        header: (
          <Box sx={{ lineHeight: 1.2 }}>
            <div>Actual</div>
            <div style={{ fontSize: "0.75rem", marginTop: "2px" }}>
              (hrs or $)
            </div>
          </Box>
        ),
      },

      {
        id: "deviation",
        header: "Deviation",
        accessorFn: (row) =>
          toNumber(row.actual) - toNumber(row.budgeted) > 0
            ? toNumber(row.actual) - toNumber(row.budgeted)
            : null,
        enableEditing: false,
      },

      { accessorKey: "remarks", header: "Remarks", enableEditing: true },
      {
        accessorKey: "actionTaken",
        header: "Action Taken",
        enableEditing: true,
      },
      {
        accessorKey: "deviationStatus",
        header: "Deviation Status",
        enableEditing: true,
        editVariant: "select",
        editSelectOptions: STATUS_OPTIONS,
        muiEditTextFieldProps: { select: true },
      },
      {
        accessorKey: "responsiblePerson",
        header: "Responsible Person",
        enableEditing: true,
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: deviationRows.filter(
      (row) => toNumber(row.actual) - toNumber(row.budgeted) > 0,
    ),

    enableGrouping: true,
    groupedColumnMode: "remove",

    initialState: {
      grouping: ["category"],
      expanded: true,
    },

    enableRowActions: true,
    positionActionsColumn: "last",

    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableStickyHeader: true,

    enableEditing: true,
    editDisplayMode: "row",

    muiEditTextFieldProps: ({ column, row }) => {
      const isGroupRow = row.getCanExpand?.();
      const editableKeys = [
        "remarks",
        "actionTaken",
        "deviationStatus",
        "responsiblePerson",
      ];

      return {
        variant: "standard",
        size: "small",
        fullWidth: true,
        disabled: isGroupRow || !editableKeys.includes(column.id),
      };
    },

    onEditingRowSave: async ({ row, values, table }) => {
      const isGroupRow = row.getCanExpand?.();
      if (isGroupRow) {
        table.setEditingRow(null);
        return;
      }

      await updateDeviationRow(row.original.id, {
        remarks: String(values.remarks ?? ""),
        actionTaken: String(values.actionTaken ?? ""),
        deviationStatus: values.deviationStatus,
        responsiblePerson: String(values.responsiblePerson ?? ""),
      });

      table.setEditingRow(null);
    },

    renderRowActions: ({ row, table }) => {
      const isGroupRow = row.getCanExpand?.();
      if (isGroupRow) return null;

      return (
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)} size="small">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    },

    displayColumnDefOptions: {
      "mrt-row-actions": { size: 60 },
      "mrt-row-expand": {
        header: "Category",
        size: 140,
        muiTableHeadCellProps: {
          sx: {
            textAlign: "left",

            "& .Mui-TableHeadCell-Content": {
              justifyContent: "flex-start",
            },

            "& .Mui-TableHeadCell-Content-Wrapper": {
              textAlign: "left",
            },

            "& .MuiTableSortLabel-root": {
              justifyContent: "flex-start",
            },
          },
        },

        Cell: ({ row, table }) => {
          if (!row.getCanExpand?.()) return null;

          const categoryTitle =
            row.groupingColumnId === "category"
              ? row.getValue("category")
              : row.getValue(row.groupingColumnId) || row.id;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MRT_ExpandButton row={row} table={table} />
              <Typography sx={{ fontWeight: 800 }}>{categoryTitle}</Typography>
            </Box>
          );
        },
      },
    },

    muiTableBodyRowProps: ({ row }) => {
      const isGroupRow = row.getCanExpand?.();
      if (!isGroupRow) return {};
      return {
        sx: {
          backgroundColor: "rgba(0,0,0,0.06)",
          "& td": { fontWeight: 800 },
        },
      };
    },

    state: {
      isLoading: deviationLoading,
      showSaving: deviationSaving,
      showProgressBars: deviationLoading,
    },

    muiTableProps: {
      sx: { tableLayout: "fixed", borderCollapse: "collapse", width: "100%" },
    },

    muiTableHeadCellProps: {
      sx: {
        borderRight: "1px solid rgba(0,0,0,0.12)",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        padding: "4px 6px",
        fontWeight: 700,
        verticalAlign: "middle",
        textAlign: "center",

        "& .Mui-TableHeadCell-Content": {
          width: "100%",
          justifyContent: "center",
        },

        "& .Mui-TableHeadCell-Content-Wrapper": {
          width: "100%",
          textAlign: "center",
        },

        "& .MuiTableSortLabel-root": {
          width: "100%",
          justifyContent: "center",
        },
      },
    },

    muiTableBodyCellProps: {
      sx: {
        borderRight: "1px solid rgba(0,0,0,0.12)",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        padding: "4px 6px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        verticalAlign: "middle",
        textAlign: "center",
      },
    },

    muiTableContainerProps: { sx: { maxWidth: "100%", overflowX: "auto" } },
    enableSorting: false,
    enableColumnActions: false,
    enableColumnOrdering: false,
    enableColumnDragging: false,
    enableColumnResizing: false,
    enableFilters: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,

    defaultColumn: {
      size: 120,
      minSize: 60,
      maxSize: 220,
    },
  });

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Deviation Report
      </Typography>
      <MaterialReactTable table={table} />
    </Box>
  );
}
